import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

// Klaviyo client API revision. Bump alongside cf-newsletter.js when upgrading.
const KLAVIYO_REVISION = '2026-07-15';
const KLAVIYO_HEADERS = {
  'Content-Type': 'application/vnd.api+json',
  accept: 'application/vnd.api+json',
  revision: KLAVIYO_REVISION,
};

/**
 * "Notify me when available" button + modal for out-of-stock variants.
 * Subscribes to Klaviyo's Back in Stock via the client API, which triggers the
 * "Subscribed to Back in Stock" metric used by the Back in Stock flow.
 *
 * Only rendered for products tagged `back-in-stock` (see snippets/back-in-stock.liquid).
 *
 * @extends {DialogComponent}
 */
class BackInStockComponent extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'emailInput', 'submitButton'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Keeps the selected variant in sync and only shows the button for sold-out variants.
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const { resource, data } = event.detail;
    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data.productId !== this.dataset.productId) {
      return;
    }

    // A null resource means the option combination doesn't exist, so there is nothing to subscribe to
    const isSoldOut = Boolean(resource) && resource.available === false;
    this.hidden = !isSoldOut;
    if (!resource) return;

    this.dataset.variantId = String(resource.id);
    if (this.refs.variantTitle) {
      const showTitle = resource.title && resource.title !== 'Default Title';
      this.refs.variantTitle.textContent = showTitle ? resource.title : '';
      this.refs.variantTitle.hidden = !showTitle;
    }

    this.#resetForm();
  };

  showDialog() {
    this.#resetForm();
    super.showDialog();
  }

  /** @param {SubmitEvent} event */
  async handleSubmit(event) {
    event.preventDefault();

    const { emailInput, marketingConsent, submitButton } = this.refs;
    const email = emailInput.value.trim();
    const { companyId, variantId, listId } = this.dataset;
    if (!email || !companyId || !variantId) return;

    submitButton.disabled = true;
    this.#setError('');

    try {
      await this.#post('back-in-stock-subscriptions', {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
          },
        },
      });

      // A BIS subscription doesn't grant marketing consent, so opt-in is a separate, explicit call
      if (listId && marketingConsent?.checked) {
        await this.#post('subscriptions', {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Back in Stock form',
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        }).catch((error) => console.error('Back in Stock marketing opt-in error:', error));
      }

      this.#showSuccess();
      this.#pushDataLayer(email, variantId);
    } catch (error) {
      console.error('Back in Stock subscription error:', error);
      this.#setError(error instanceof KlaviyoError && error.detail ? error.detail : this.dataset.errorText || '');
    } finally {
      submitButton.disabled = false;
    }
  }

  /**
   * @param {string} endpoint
   * @param {object} payload
   */
  async #post(endpoint, payload) {
    const response = await fetch(`https://a.klaviyo.com/client/${endpoint}?company_id=${this.dataset.companyId}`, {
      method: 'POST',
      headers: KLAVIYO_HEADERS,
      body: JSON.stringify(payload),
    });

    // Klaviyo returns 202 with an empty body on success
    if (response.status === 202) return;

    const body = await response.json().catch(() => null);
    throw new KlaviyoError(response.status, body?.errors?.[0]?.detail);
  }

  #showSuccess() {
    const { form, successMessage } = this.refs;
    form.hidden = true;
    if (successMessage) successMessage.hidden = false;
  }

  #resetForm() {
    const { form, successMessage } = this.refs;
    if (!form) return;
    form.hidden = false;
    if (successMessage) successMessage.hidden = true;
    this.#setError('');
  }

  /** @param {string} message */
  #setError(message) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;
    errorMessage.textContent = message;
    errorMessage.hidden = !message;
  }

  /**
   * @param {string} email
   * @param {string} variantId
   */
  #pushDataLayer(email, variantId) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'dl_subscribe',
        form_id: 'product_back_in_stock_signup',
        lead_type: 'email',
        email,
        variant_id: variantId,
        product_id: this.dataset.productId,
      });
    } catch (e) {}
  }
}

class KlaviyoError extends Error {
  /**
   * @param {number} status
   * @param {string} [detail]
   */
  constructor(status, detail) {
    super(`Klaviyo ${status}: ${detail ?? 'Unknown error'}`);
    this.detail = detail;
  }
}

if (!customElements.get('back-in-stock-component')) {
  customElements.define('back-in-stock-component', BackInStockComponent);
}
